import { useCallback } from "react";
import { saveAs } from "file-saver";

export const useExcelExport = () => {
  const exportToExcel = useCallback(
    async (data, worksheetName = "Datos", fileName = "archivo", options = {}) => {
      const XLSX = await import("xlsx");
      const { title, subtitle } = options;

      let worksheet;
      let startRow = 0;

      if (title || subtitle) {
        // Obtener el número de columnas de los datos
        const numCols = data && data.length > 0 ? Object.keys(data[0]).length : 1;
        
        // Crear worksheet vacío
        worksheet = XLSX.utils.aoa_to_sheet([]);
        
        // Agregar título
        if (title) {
          // Crear una fila con el título repetido en todas las columnas
          const titleRow = new Array(numCols).fill(title);
          XLSX.utils.sheet_add_aoa(worksheet, [titleRow], { origin: 0 });
          
          // Merge de las celdas del título
          if (!worksheet['!merges']) worksheet['!merges'] = [];
          worksheet['!merges'].push({
            s: { r: 0, c: 0 },
            e: { r: 0, c: numCols - 1 }
          });
          
          startRow++;
        }
        
        // Agregar subtítulo
        if (subtitle) {
          // Crear una fila con el subtítulo repetido en todas las columnas
          const subtitleRow = new Array(numCols).fill(subtitle);
          XLSX.utils.sheet_add_aoa(worksheet, [subtitleRow], { origin: startRow });
          
          // Merge de las celdas del subtítulo
          if (!worksheet['!merges']) worksheet['!merges'] = [];
          worksheet['!merges'].push({
            s: { r: startRow, c: 0 },
            e: { r: startRow, c: numCols - 1 }
          });
          
          startRow++;
        }
        
        // Agregar una fila vacía de separación
        if (title || subtitle) {
          const emptyRow = new Array(numCols).fill('');
          XLSX.utils.sheet_add_aoa(worksheet, [emptyRow], { origin: startRow });
          
          // Merge de la fila vacía
          if (!worksheet['!merges']) worksheet['!merges'] = [];
          worksheet['!merges'].push({
            s: { r: startRow, c: 0 },
            e: { r: startRow, c: numCols - 1 }
          });
          
          startRow++;
        }
        
        // Agregar los datos
        XLSX.utils.sheet_add_json(worksheet, data, { origin: startRow, skipHeader: false });
      } else {
        // Sin encabezado, crear worksheet directamente
        worksheet = data
          ? XLSX.utils.json_to_sheet(data)
          : XLSX.utils.aoa_to_sheet(data);
      }

      // Ajustar automáticamente el ancho de las columnas y formatear números
      const columnWidths = [];
      
      if (data && data.length > 0) {
        // Obtener las claves (nombres de columnas)
        const keys = Object.keys(data[0]);
        
        keys.forEach((key, index) => {
          // Calcular el ancho máximo entre el encabezado y los valores
          let maxWidth = key.length;
          
          data.forEach((row, rowIndex) => {
            const cellValue = row[key];
            const cellLength = cellValue ? String(cellValue).length : 0;
            maxWidth = Math.max(maxWidth, cellLength);
            
            // Formatear celdas numéricas
            if (typeof cellValue === 'number' && !Number.isNaN(cellValue)) {
              const cellAddress = XLSX.utils.encode_cell({ 
                r: rowIndex + startRow, 
                c: index 
              });
              
              if (worksheet[cellAddress]) {
                worksheet[cellAddress].t = 'n'; // Tipo número
                worksheet[cellAddress].z = '#,##0.00'; // Formato con 2 decimales
              }
            }
          });
          
          // Agregar un margen y limitar el ancho máximo
          columnWidths.push({ wch: Math.min(maxWidth + 2, 50) });
        });
        
        worksheet['!cols'] = columnWidths;
      }

      // create a new workbook
      const workbook = XLSX.utils.book_new();

      // add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);

      // write the workbook to an array buffer
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      // convert the array buffer to a blob
      const dataBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // save the blob to a file
      saveAs(dataBlob, `${fileName}.xlsx`);
    },
    []
  );

  return { exportToExcel };
};
